import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/primitives';
import { DataTable, type Column } from '~/components/shared/data-table';
import { PageHeader } from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys } from '~/lib/queries';
import { formatDateTime } from '~/lib/utils';
import { type AuditEntry } from '@hch/shared';

const COLONNES: Array<Column<AuditEntry>> = [
  {
    header: 'Horodatage',
    cell: (entree) => formatDateTime(entree.timestamp),
    nowrap: true,
    className: 'text-xs tabular-nums',
  },
  {
    header: 'Action',
    cell: (entree) => (
      <Badge variant="outline" className="font-mono text-[10px]">
        {entree.action}
      </Badge>
    ),
  },
  {
    header: 'Entite',
    cell: (entree) => (
      <>
        {entree.entity}
        {entree.entityId ? (
          <span className="ml-1 font-mono text-[10px] text-muted-foreground">
            {entree.entityId.slice(0, 8)}
          </span>
        ) : null}
      </>
    ),
    className: 'text-sm',
  },
  {
    header: 'Auteur',
    cell: (entree) =>
      entree.actor ? (
        <>
          {entree.actor.email}
          <br />
          <span className="text-muted-foreground">{entree.actor.role}</span>
        </>
      ) : (
        <span className="text-muted-foreground">Systeme</span>
      ),
    className: 'text-xs',
  },
];

export function AdminAuditPage() {
  const [entity, setEntity] = React.useState('');
  const [page, setPage] = React.useState(1);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.audit({ entity, page }),
    queryFn: () =>
      api.get<{ items: AuditEntry[]; total: number; page: number; perPage: number }>(
        '/admin/audit',
        {
          entity: entity || undefined,
          page,
          perPage: 25,
        },
      ),
  });

  return (
    <div className="app-page">
      <PageHeader
        title="Journal d'audit"
        description="Trace de toutes les modifications : qui, quoi, quand. Conserve un an, purge automatique."
      />

      <div className="flex flex-wrap gap-2">
        <Select
          value={entity || 'all'}
          onValueChange={(v) => {
            setEntity(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Toutes les entites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les entites</SelectItem>
            {['Intervention', 'Zone', 'User', 'Package', 'Product', 'ScheduleTemplate', 'Slot'].map(
              (item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={COLONNES}
        rows={data?.items}
        rowKey={(entree) => `${entree.timestamp}-${entree.action}-${entree.entityId ?? ''}`}
        isLoading={isLoading}
        emptyTitle="Aucune entree"
        emptyDescription="Le journal se remplit au fil des modifications."
      />

      {data && data.items.length > 0 ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {data.total} entree{data.total > 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((value) => value - 1)}
              >
                Precedent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page * data.perPage >= data.total}
                onClick={() => setPage((value) => value + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
